import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { setOrganizationId } from '@/api/client';
import { supabase } from '@/lib/supabase';

interface Organization {
  id: number;
  uuid: string;
  name: string;
  slug: string;
  currency: string;
  timezone: string;
  is_active: boolean;
  created_at: string;
}

interface OrganizationContextType {
  organization: Organization | null;
  organizations: Organization[];
  loading: boolean;
  error: string | null;
  createOrganization: (data: { name: string; currency?: string; timezone?: string }) => Promise<{ error: Error | null }>;
  switchOrganization: (orgId: string) => void;
  refreshOrganizations: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

// Use relative path to go through Vite proxy
const API_URL: string = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshOrganizations = async () => {
    console.log('[useOrganization] refreshOrganizations called');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      console.log('[useOrganization] session:', session ? 'exists' : 'null', 'accessToken:', session?.access_token ? 'present' : 'missing');
      
      if (!session) {
        console.log('[useOrganization] No session, clearing organizations');
        setOrganizations([]);
        setOrganization(null);
        setLoading(false);
        return;
      }

      const url = `${API_URL}/organizations/`;
      console.log('[useOrganization] Fetching:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      console.log('[useOrganization] Response status:', response.status);

      // Handle 401 - user will be redirected by apiFetch, but handle gracefully here too
      if (response.status === 401) {
        console.log('[useOrganization] 401 Unauthorized, clearing organizations');
        setOrganizations([]);
        setOrganization(null);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[useOrganization] Failed to fetch organizations:', response.status, errorText);
        throw new Error('Failed to fetch organizations');
      }

      const data = await response.json() as Organization[];
      console.log('[useOrganization] Received', data.length, 'organizations:', data);
      setOrganizations(data);

      // Set current organization from localStorage or first org
      const storedOrgId = localStorage.getItem('currentOrganizationId');
      const currentOrg = storedOrgId
        ? data.find((org: Organization) => org.uuid === storedOrgId)
        : data[0];

      if (currentOrg) {
        console.log('[useOrganization] Setting current organization:', currentOrg.name);
        setOrganization(currentOrg);
        setOrganizationId(currentOrg.id);
        localStorage.setItem('currentOrganizationId', currentOrg.uuid);
      }

      setError(null);
    } catch (err) {
      // Don't throw - just set empty organizations
      console.error('[useOrganization] Error:', err);
      setOrganizations([]);
      setOrganization(null);
      setError(err instanceof Error ? err.message : 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const createOrganization = async (data: { name: string; currency?: string; timezone?: string }) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        return { error: new Error('Not authenticated') };
      }

      const response = await fetch(`${API_URL}/api/organizations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: data.name,
          currency: data.currency ?? 'CAD',
          timezone: data.timezone ?? 'America/Toronto',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json() as { detail?: string };
        return { error: new Error(errorData.detail ?? 'Failed to create organization') };
      }

      const newOrg = await response.json() as Organization;
      setOrganizations((prev) => [...prev, newOrg]);
      setOrganization(newOrg);
      setOrganizationId(newOrg.id);
      localStorage.setItem('currentOrganizationId', newOrg.uuid);

      return { error: null };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('Failed to create organization') };
    }
  };

  const value = useMemo(() => ({
    organization,
    organizations,
    loading,
    error,
    createOrganization,
    refreshOrganizations,
    switchOrganization: (orgId: string) => {
      const org = organizations.find((o) => o.uuid === orgId);
      if (org) {
        setOrganization(org);
        setOrganizationId(org.id);
        localStorage.setItem('currentOrganizationId', orgId);
      }
    },
  }), [organization, organizations, loading, error]);

  useEffect(() => {
    void refreshOrganizations();
  }, []);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}